/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

export type AgentPillProps = {
	label: string;
	icon: string;
	/**
	 * The product home page URL the pill should link to.
	 */
	url: string;
	/**
	 * How the agent icon image should fit inside its 16x16 white tile.
	 * Defaults to `'cover'`.
	 */
	objectFit?: 'cover' | 'contain';
};

const styles = cssMap({
	pill: {
		display: 'inline-flex',
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	pillLink: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.075'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderRadius: '6px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#1F1F21',
		height: '32px',
		textDecoration: 'none',
		cursor: 'pointer',
		transition: 'background-color 150ms ease, transform 150ms ease',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: '#292A2E',
		},
		'&:active': {
			transform: 'scale(0.98)',
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	pillIconTile: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#FFFFFF',
		borderRadius: token('radius.xsmall'),
		overflow: 'hidden',
	},
	pillIconImage: {
		display: 'block',
		width: '100%',
		height: '100%',
	},
	pillIconImageContain: {
		objectFit: 'contain',
	},
	pillIconImageCover: {
		objectFit: 'cover',
	},
	pillLabel: {
		font: token('font.body'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#DDDEE1',
		fontFamily: token('font.family.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '28px',
		whiteSpace: 'nowrap',
	},
});

export const AgentPill = ({
	label,
	icon,
	url,
	objectFit = 'cover',
}: AgentPillProps): JSX.Element => (
	<li css={styles.pill}>
		{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
		<a css={styles.pillLink} href={url} target="_blank" rel="noopener noreferrer">
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<span css={styles.pillIconTile}>
				<img
					css={[
						styles.pillIconImage,
						objectFit === 'contain' ? styles.pillIconImageContain : styles.pillIconImageCover,
					]}
					src={icon}
					alt=""
					aria-hidden="true"
				/>
			</span>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<span css={styles.pillLabel}>{label}</span>
		</a>
	</li>
);
